import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import dayjs from "dayjs";
import moment from "moment";
import { z } from "zod";

import { useAppointments } from "components/AppointmentsList/hooks/useAppointments";
import { CustomCalendar } from "components/CustomCalendar/CustomCalendar";
import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { PatientAutocomplete } from "components/ui/PatientAutocomplete/PatientAutocomplete";
import { Select } from "components/ui/Select/Select";
import { useAddAppointment } from "features/appointments/hooks/useAddAppointment";
import { useAppointment } from "features/appointments/hooks/useAppointment";
import {
  findOverlappingAppointments,
  getMaxOverlapDepth,
  MAX_CONCURRENT_APPOINTMENTS,
} from "features/appointments/utils/hasAppointmentOverlap";
import { useActiveLocations } from "features/locations/hooks/useActiveLocations";
import { useClientSearch } from "features/patients/hooks/useClientSearch";
import { useConsultTypes } from "features/practice/hooks/useConsultTypes";
import { usePracticeSettings } from "features/practice/hooks/usePracticeSettings";
import { HealthMailContactOrEmailField } from "features/results/components/HealthMailContactOrEmailField/HealthMailContactOrEmailField";
import { REFERRER_CONTACT_TITLES } from "features/results/constants/healthMailContactTitles";
import {
  contactEmailSchema,
  contactIdSchema,
  refineContactOrEmail,
} from "features/results/utils/contactOrEmail";
import { financialClasses } from "utils/patientFormOptions";
import { reportFormErrors } from "utils/reportFormErrors";
import { pathGenerators } from "utils/routes";

import style from "./AddAppointmentForm.module.css";
import { useGetDurationOptions } from "./useGetdurationOptions";

// Raw consultation-type values (e.g. "PHONE", "IN_PERSON") → readable labels.
const formatConsultationType = (type) => {
  if (!type) return "";

  return type
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formSchema = z.object({
  notes: z.string(),
  doctor: z.string().min(1, { message: "Please select doctor." }),
  /*
   * Required only when the picker is actually shown — see the check in handleFormSubmit.
   * Demanding it here made every hidden-picker case (a single-location practice, a phone or
   * video appointment, no locations configured at all) fail validation on a field nobody
   * could see or fill, which reads as a button that does nothing.
   */
  location: z.string().optional(),
  patient: z
    .string({ required_error: "Please select patient." })
    .min(1, { message: "Please select patient." }),
  appointmentType: z
    .string()
    .min(1, { message: "Please select appointment type." }),
  consultationType: z
    .string()
    .min(1, { message: "Please select consultation type." }),
  date: z.string().min(1, { message: "Please select date." }),
  time: z.string().min(1, { message: "Please select time." }),
  duration: z.string().min(1, { message: "Please select duration." }),
  telephoneNumber: z.string().optional(),
  email: z.string().email().optional().or(z.literal("")),
  patientClassification: z.string().optional(),
  medicalCardNumber: z.string().optional(),
  referrerContactId: contactIdSchema,
  referrerEmail: contactEmailSchema,
}).superRefine(
  refineContactOrEmail({
    contactField: "referrerContactId",
    emailField: "referrerEmail",
  })
);

const getReferrerValues = (appointment) => ({
  referrerContactId:
    appointment?.referrerContactId ?? appointment?.referrer?.id ?? null,
  referrerEmail: appointment?.referrerEmail ?? "",
});

export const AddAppointmentForm = ({
  initialData,
  closeModal,
  allTimes,
  services,
  staffMembers,
  initialStaffMember,
  initialStartDate,
  initialStartTime,
  refetchCalendar,
  slotDurationMinutes,
}) => {
  const [selectedPatient, setSelectedPatient] = useState(() => {
    const p = initialData?.patient;
    if (!p?.id) return null;
    return {
      id: p.id,
      title: `${[p.firstName, p.surname].filter(Boolean).join(" ")}${
        p.dateOfBirth ? ` (${moment(p.dateOfBirth).format("DD-MM-YYYY")})` : ""
      }`,
    };
  });
  const [searchTerm, setSearchTerm] = useState(
    initialData?.patient?.surname || ""
  );
  const [dateValue, setDateValue] = useState(() => {
    if (initialData?.date) {
      // ISO since KIN-2174; dayjs reads it natively (see formatAppointment.js).
      return dayjs(initialData.date).toDate();
    }
    if (initialStartDate) {
      return dayjs(initialStartDate, "DD-MM-YYYY").toDate();
    }
    return new Date();
  });

  const { data: patients } = useClientSearch(searchTerm);
  const {
    data: consultTypes,
    isLoading: isLoadingConsultTypes,
    isError: isConsultTypesError,
    refetch: refetchConsultTypes,
  } = useConsultTypes();

  const consultTypeOptions = (() => {
    const fetched = (consultTypes || []).map((value) => ({
      label: formatConsultationType(value),
      value,
    }));
    const initialValue =
      initialData?.consultationType ||
      initialData?.consultation?.consultationType;
    if (
      initialValue &&
      !fetched.some((option) => option.value === initialValue)
    ) {
      return [
        ...fetched,
        { label: formatConsultationType(initialValue), value: initialValue },
      ];
    }

    return fetched;
  })();
  const hasNoConsultTypes =
    !isLoadingConsultTypes &&
    !isConsultTypesError &&
    consultTypeOptions.length === 0;

  // Locations available for booking. The dropdown always shows (for an
  // in-person appointment) so the location is visible either way; with only
  // one to choose from it's disabled (read-only) since there's nothing to
  // pick — the auto-select effect below still fills it in. With none
  // configured at all there's nothing to show or select, so the field is
  // left out entirely.
  const { data: locations } = useActiveLocations();
  const locationOptions = (locations || []).map((location) => ({
    label: location.name,
    value: String(location.id),
  }));
  const defaultLocationId =
    locations?.find((location) => location.isDefault)?.id ??
    locations?.[0]?.id;

  useEffect(() => {
    if (
      patients &&
      patients.length &&
      initialData?.patient?.id &&
      !selectedPatient
    ) {
      const selectedPatientData = patients.find(
        (patient) => patient.id === initialData?.patient?.id
      );

      if (selectedPatientData) {
        setSelectedPatient({
          id: selectedPatientData.id,
          title: `${selectedPatientData.firstName}${
            selectedPatientData.surname ? " " + selectedPatientData.surname : ""
          }${
            selectedPatientData.dateOfBirth
              ? ` (${moment(selectedPatientData.dateOfBirth).format(
                  "DD-MM-YYYY"
                )})`
              : ""
          }`,
        });
      }
    }
  }, [patients, initialData?.patient?.surname, selectedPatient, initialData]);

  /*
   * The calendar hands this form a slimmed copy of the appointment, and the save writes the
   * referrer on every call: whatever is missing is cleared (KIN-2211). So an edit reads the
   * stored referrer from the appointment itself, and cannot be saved until it has.
   */
  const isEditing = initialData?.id != null;
  const {
    data: loadedAppointment,
    isLoading: isLoadingAppointment,
    isError: isAppointmentError,
    refetch: refetchAppointment,
  } = useAppointment(isEditing ? initialData.id : null);
  const isReferrerUnknown =
    isEditing && (isLoadingAppointment || isAppointmentError);

  // Switched on in Settings → Practice Settings (KIN-2211).
  const { data: practiceSettings } = usePracticeSettings();
  const showReferrerField = practiceSettings?.collectAppointmentReferrals === true;

  const { mutate: addAppointment, isPending } = useAddAppointment({
    onSuccess: () => {
      closeModal();
      form.reset();
      refetchCalendar?.();
    },
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      // Editing an existing appointment shows THAT appointment's staff
      // member. For a new appointment opened from a doctor-specific
      // column, we use that column's doctor (initialStaffMember). With
      // no column context we fall back to the first doctor in the
      // dropdown — explicitly NOT the logged-in user. The old
      // template-literal default also produced the string "undefined"
      // when initialStaffMember was missing, which silently leaked into
      // submit.
      doctor:
        initialData?.staffMemberId != null
          ? String(initialData.staffMemberId)
          : initialStaffMember != null
          ? String(initialStaffMember)
          : staffMembers?.[0]?.id != null
          ? String(staffMembers[0].id)
          : "",
      patient: initialData?.patient?.id?.toString() || "",
      appointmentType:
        initialData?.practiceServiceId?.toString() ||
        services?.at(0)?.id?.toString() ||
        "",
      date: initialData?.date
        ? dayjs(initialData.date).format("YYYY-MM-DD")
        : initialStartDate
        ? dayjs(initialStartDate, "DD-MM-YYYY").format("YYYY-MM-DD")
        : format(new Date(), "yyyy-MM-dd"),
      time: initialData?.time || initialStartTime || "",
      duration: initialData?.durationInMins?.toString() || "",
      consultationType:
        initialData?.consultationType ||
        initialData?.consultation?.consultationType ||
        "",
      location: initialData?.locationId != null
        ? String(initialData.locationId)
        : "",
      notes: initialData?.reasonDescription || "",
      telephoneNumber: initialData?.telephoneNumber || "",
      email: initialData?.email || "",
      patientClassification: "",
      medicalCardNumber: "",
      ...getReferrerValues(initialData),
    },
  });

  // Fill in the stored referrer once the appointment has loaded, unless the user has
  // already chosen one: their choice wins over the stored value.
  useEffect(() => {
    if (!loadedAppointment) return;

    const isReferrerTouched =
      form.getFieldState("referrerContactId").isDirty ||
      form.getFieldState("referrerEmail").isDirty;

    if (isReferrerTouched) return;

    const referrerValues = getReferrerValues(loadedAppointment);
    form.setValue("referrerContactId", referrerValues.referrerContactId);
    form.setValue("referrerEmail", referrerValues.referrerEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedAppointment]);

  // A video or phone appointment has no physical location to book — the
  // field would either be meaningless or force staff to pick one that has
  // no bearing on the appointment. With exactly one location, hide the
  // picker entirely (there's nothing to choose) — the auto-fill effect
  // below still sets the default location for in-person appointments.
  const isRemoteConsultationType = ["VIDEO", "PHONE"].includes(
    form.watch("consultationType")
  );
  const showLocationPicker =
    locationOptions.length > 1 && !isRemoteConsultationType;

  useEffect(() => {
    if (initialData?.id) return;
    if (form.getValues("consultationType")) return;
    if (consultTypeOptions.length === 0) return;

    form.setValue("consultationType", consultTypeOptions[0].value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultTypeOptions.length, initialData?.id]);

  // Same auto-select idiom as consultationType above: once locations load,
  // silently fill in the default (or only) one unless a value is already
  // set. Runs regardless of consultation type/picker visibility, so the
  // value is already correct if the consultation type is later changed back
  // to an in-person one (see showLocationPicker).
  //
  // This deliberately runs when editing as well. It used to bail out on initialData?.id, so an
  // appointment whose locationId was null — every appointment booked before locations existed —
  // opened with an empty location and could never be saved again. The getValues guard below is
  // what protects the appointment's own location; the id check only ever suppressed the fix.
  useEffect(() => {
    if (form.getValues("location")) return;
    if (!defaultLocationId) return;

    form.setValue("location", String(defaultLocationId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLocationId, initialData?.id]);

  const allowOverlappingAppointments =
    !!practiceSettings?.allowOverlappingAppointments;

  const watchedDoctor = form.watch("doctor");
  const watchedDate = form.watch("date");

  // Existing bookings for the selected doctor on the selected day — the set the
  // new/edited appointment is checked against for overlap.
  const { data: doctorScheduleData } = useAppointments({
    is5minsAppointment: slotDurationMinutes === 5,
    selectedStartDate: watchedDate ? dayjs(watchedDate).toDate() : new Date(),
    selectedEndDate: watchedDate ? dayjs(watchedDate).toDate() : new Date(),
    selectedDoctors: watchedDoctor ? [String(watchedDoctor)] : [],
    enabled: !!watchedDoctor && !!watchedDate,
  });

  const doctorSchedule = useMemo(
    () =>
      (doctorScheduleData?.data || [])
        .flatMap((day) => day.doctors || [])
        .filter((doctor) => String(doctor.staffMemberId) === String(watchedDoctor))
        .flatMap((doctor) => doctor.schedule || []),
    [doctorScheduleData, watchedDoctor]
  );

  const [pendingValues, setPendingValues] = useState(null);
  const [overlapCount, setOverlapCount] = useState(0);

  const submitAppointment = (values) => {
    addAppointment({
      id: initialData?.id,
      patientId: values.patient,
      /*
       * The appointment's own reason for visit, kept as it was. This form has no field for
       * it: Notes is the reason description. Copying the notes in as well showed the same
       * text twice in the consult box, under "Reason for Visit" and "Reason Description"
       * (KIN-2198).
       */
      reasonForVisit: initialData?.reasonForVisit ?? null,
      startDate: values.date,
      startTime: values.time,
      durationInMins: values.duration,
      practiceServiceId: values.appointmentType,
      consultationType: values.consultationType,
      locationId: values.location,
      reasonDescription: values.notes,
      staffMemberId: values.doctor,
      telephoneNumber: values.telephoneNumber,
      email: values.email,
      // Sent whether or not the field is on screen: hiding it must not clear it.
      referrerContactId: values.referrerContactId,
      referrerEmail: values.referrerEmail,
    });
  };

  const handleInvalidSubmit = (errors) =>
    reportFormErrors({ formName: "AddAppointmentForm", errors });

  const handleFormSubmit = (values) => {
    if (isReferrerUnknown) return;

    // The one field the schema cannot judge on its own: it is required exactly when it is on
    // screen. setError puts the message under the picker the user is looking at.
    if (showLocationPicker && !values.location) {
      form.setError("location", {
        type: "manual",
        message: "Please select location.",
      });
      handleInvalidSubmit({ location: { message: "Please select location." } });

      return;
    }

    const startAt = moment(`${values.date} ${values.time}`).toDate();
    const endAt = moment(`${values.date} ${values.time}`)
      .add(Number(values.duration), "minutes")
      .toDate();

    const overlaps = findOverlappingAppointments({
      schedule: doctorSchedule,
      startAt,
      endAt,
      excludeAppointmentId: initialData?.id,
    });

    if (overlaps.length === 0) {
      submitAppointment(values);
      return;
    }

    // Hard cap: a doctor can never have more than MAX_CONCURRENT_APPOINTMENTS
    // appointments running at the same instant. Booking this one would push the
    // peak past the cap → block regardless of the overlapping-appointments
    // setting.
    const resultingConcurrency =
      getMaxOverlapDepth({ overlaps, startAt, endAt }) + 1;

    if (resultingConcurrency > MAX_CONCURRENT_APPOINTMENTS) {
      toast.error(
        `A slot can hold at most ${MAX_CONCURRENT_APPOINTMENTS} overlapping appointments for a doctor.`
      );
      return;
    }

    // Within the cap but still overlapping: block unless the practice allows
    // overlapping bookings.
    if (!allowOverlappingAppointments) {
      toast.error(
        "This time overlaps an existing appointment for this doctor. Enable “Allow Overlapping Appointments” in Practice Settings to book anyway."
      );
      return;
    }

    setOverlapCount(overlaps.length);
    setPendingValues(values);
  };

  const handleConfirmOverlap = () => {
    if (pendingValues) submitAppointment(pendingValues);
    setPendingValues(null);
  };

  const handleCancelOverlap = () => setPendingValues(null);

  const {
    durationOptions,
    consultTimes,
    isLoading: isLoadingConsultTimes,
    isError: isConsultTimesError,
    refetch: refetchConsultTimes,
    hasNoConsultTimes,
  } = useGetDurationOptions(
    form.watch("time"),
    allTimes,
    form.watch("appointmentType"),
    slotDurationMinutes,
    initialData?.durationInMins
  );

  useEffect(() => {
    const appointmentType = form.watch("appointmentType");
    const currentDuration = form.watch("duration");

    const selectedService = services?.find(
      (service) => service.id?.toString() === appointmentType
    );
    const isVaccination =
      selectedService?.serviceName?.toLowerCase().includes("vaccine") ||
      selectedService?.serviceName?.toLowerCase().includes("vaccination");

    if (
      !initialData?.durationInMins &&
      isVaccination &&
      currentDuration === "15"
    ) {
      form.setValue("duration", "5");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form.watch in deps causes infinite loop
  }, [form.watch("appointmentType"), services, initialData?.durationInMins]);

  useEffect(() => {
    if (selectedPatient && selectedPatient.id) {
      const fullPatientData = patients?.find(
        (p) => p.id === selectedPatient.id
      );

      if (fullPatientData) {
        if (!initialData?.telephoneNumber && fullPatientData.mobilePhone) {
          form.setValue("telephoneNumber", fullPatientData.mobilePhone);
        }

        if (!initialData?.email && fullPatientData.email) {
          form.setValue("email", fullPatientData.email);
        }

        const financialClassOption = financialClasses.find(
          (option) => option.value === fullPatientData.financialClass
        );
        form.setValue(
          "patientClassification",
          financialClassOption?.label || ""
        );
        form.setValue(
          "medicalCardNumber",
          fullPatientData.medicalCardNumber || ""
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient?.id]);

  useEffect(() => {
    if (
      !initialData?.time &&
      !initialStartTime &&
      allTimes?.length &&
      !form.getValues("time")
    ) {
      form.setValue("time", allTimes[0].formattedRange.split(" - ")[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTimes]);

  useEffect(() => {
    if (
      !initialData?.durationInMins &&
      consultTimes.length > 0 &&
      !form.getValues("duration")
    ) {
      form.setValue("duration", String(consultTimes[0]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultTimes]);

  return (
    <>
      <FormProvider {...form}>
        <form
          className={style.addLetterForm}
          onSubmit={form.handleSubmit(handleFormSubmit, handleInvalidSubmit)}
        >
          <div className={style.inputGroupGrid} style={{ maxWidth: "100%" }}>
            <FormField name="doctor">
              <label className={style.addLetterFormLabel}>Doctor</label>
              <Select
                name="doctor"
                options={(staffMembers || []).map((staffMember) => ({
                  label: `${staffMember.active ? "● " : ""}${
                    staffMember.firstName
                  } ${staffMember.lastName}${
                    staffMember.staffRole
                      ? ` (${staffMember.staffRole.toLowerCase()})`
                      : ""
                  }`,
                  value: String(staffMember.id),
                }))}
                value={form.watch("doctor")}
                onChange={(value) => form.setValue("doctor", value)}
                placeholder={null}
                fullWidth
              />
            </FormField>
            <FormField name="patient">
              <label className={style.addLetterFormLabel}>Patient</label>
              <PatientAutocomplete
                id="combo-box-demo"
                value={selectedPatient}
                onChange={(newValue) => {
                  setSelectedPatient(newValue);
                  // Empty string, not undefined: zod reports a missing key with its own
                  // "Required" and never reaches the message below.
                  form.setValue("patient", newValue?.id?.toString() ?? "");
                }}
                onSearchTermChange={setSearchTerm}
                disabled={!!initialData?.id}
              />
            </FormField>
          </div>
          <div className={style.inputGroupGrid4}>
            <FormField name="appointmentType">
              <label className={style.addLetterFormLabel}>
                Appointment Type
              </label>
              <Select
                name="appointmentType"
                options={(services || []).map((service) => ({
                  label: service.name,
                  value: String(service.id),
                }))}
                value={form.watch("appointmentType")}
                onChange={(value) => form.setValue("appointmentType", value)}
                placeholder={null}
                fullWidth
              />
            </FormField>
            <FormField name="date">
              <label className={style.addLetterFormLabel}>Date</label>
              <CustomCalendar
                value={dateValue}
                onChange={(date) => {
                  setDateValue(date);
                  if (date) {
                    form.setValue("date", moment(date).format("YYYY-MM-DD"));
                  }
                }}
                placeholder="Select appointment date"
              />
            </FormField>
            <FormField name="time">
              <label className={style.addLetterFormLabel}>Time</label>
              <Select
                name="time"
                options={(allTimes || []).map((time) => ({
                  label: time.formattedRange.split(" - ")[0],
                  value: time.formattedRange.split(" - ")[0],
                }))}
                value={form.watch("time")}
                onChange={(value) => form.setValue("time", value)}
                placeholder={null}
                fullWidth
              />
            </FormField>
            <FormField name="duration">
              <label className={style.addLetterFormLabel}>Duration</label>
              <Select
                name="duration"
                options={durationOptions}
                value={form.watch("duration")}
                onChange={(value) => form.setValue("duration", value)}
                placeholder={
                  isLoadingConsultTimes
                    ? "Loading…"
                    : isConsultTimesError
                    ? "Couldn't load durations"
                    : hasNoConsultTimes
                    ? "No durations configured"
                    : null
                }
                disabled={
                  isLoadingConsultTimes ||
                  isConsultTimesError ||
                  hasNoConsultTimes
                }
                fullWidth
              />
              {isConsultTimesError && (
                <p className={style.consultTypesHint}>
                  Couldn't load durations.{" "}
                  <button
                    type="button"
                    className={style.consultTypesRetry}
                    onClick={() => refetchConsultTimes()}
                  >
                    Retry
                  </button>
                </p>
              )}
              {hasNoConsultTimes && (
                <p className={style.consultTypesHint}>
                  No appointment durations configured. Set them in{" "}
                  <Link to={pathGenerators.settings.practiceSettings()}>
                    Practice Settings
                  </Link>
                  .
                </p>
              )}
            </FormField>
          </div>
          <div className={style.inputGroupGrid4}>
            <FormField name="consultationType">
              <label className={style.addLetterFormLabel}>
                Consultation Type
              </label>
              <Select
                name="consultationType"
                options={consultTypeOptions}
                value={form.watch("consultationType")}
                onChange={(value) => form.setValue("consultationType", value)}
                placeholder={
                  isLoadingConsultTypes
                    ? "Loading…"
                    : isConsultTypesError
                    ? "Couldn't load types"
                    : hasNoConsultTypes
                    ? "No types configured"
                    : "Select"
                }
                disabled={
                  isLoadingConsultTypes ||
                  isConsultTypesError ||
                  hasNoConsultTypes
                }
                fullWidth
              />
              {isConsultTypesError && (
                <p className={style.consultTypesHint}>
                  Couldn't load consultation types.{" "}
                  <button
                    type="button"
                    className={style.consultTypesRetry}
                    onClick={() => refetchConsultTypes()}
                  >
                    Retry
                  </button>
                </p>
              )}
              {hasNoConsultTypes && (
                <p className={style.consultTypesHint}>
                  No consultation types configured. Set them in{" "}
                  <Link to={pathGenerators.settings.practiceSettings()}>
                    Practice Settings
                  </Link>
                  .
                </p>
              )}
            </FormField>
            <FormField name="telephoneNumber">
              <label className={style.addLetterFormLabel}>
                Telephone Number
              </label>
              <input
                name="telephoneNumber"
                type="tel"
                className="form-control"
                placeholder="Enter telephone number"
                {...form.register("telephoneNumber")}
              />
            </FormField>
            <FormField name="email">
              <label className={style.addLetterFormLabel}>Email Address</label>
              <input
                name="email"
                type="email"
                className="form-control"
                placeholder="Enter email address"
                {...form.register("email")}
              />
            </FormField>
            <FormField name="patientClassification">
              <label className={style.addLetterFormLabel}>
                Patient Classification
              </label>
              <input
                name="patientClassification"
                type="text"
                className="form-control"
                disabled
                {...form.register("patientClassification")}
              />
            </FormField>
            {/* Only shown when the selected patient actually has one on file
                — an empty, always-visible field here would read as broken
                rather than "not recorded". */}
            {!!form.watch("medicalCardNumber") && (
              <FormField name="medicalCardNumber">
                <label className={style.addLetterFormLabel}>
                  Medical Card Number
                </label>
                <input
                  name="medicalCardNumber"
                  type="text"
                  className="form-control"
                  disabled
                  {...form.register("medicalCardNumber")}
                />
              </FormField>
            )}
          </div>
          {/* Location and referrer share the row below Consultation Type. Location is hidden
              for video/phone appointments (no physical location to book),
              when the practice has no locations configured, or when there's
              only one location (nothing to choose). The auto-fill effect
              above still sets the default location for in-person appointments
              even when this picker is hidden. */}
          {(showLocationPicker || showReferrerField) && (
            <div className={style.inputGroupGrid4}>
              {showLocationPicker && (
                <FormField name="location">
                  <label className={style.addLetterFormLabel}>Location</label>
                  <Select
                    name="location"
                    options={locationOptions}
                    value={form.watch("location")}
                    onChange={(value) => form.setValue("location", value)}
                    placeholder={null}
                    fullWidth
                  />
                </FormField>
              )}
              {/* Sent whether or not it is shown, so hiding it never clears it. */}
              {showReferrerField && (
                <HealthMailContactOrEmailField
                  contactName="referrerContactId"
                  emailName="referrerEmail"
                  label="Referrer"
                  titles={REFERRER_CONTACT_TITLES}
                  initialContact={
                    loadedAppointment?.referrer ?? initialData?.referrer
                  }
                  labelClassName={style.addLetterFormLabel}
                />
              )}
            </div>
          )}
          {isEditing && isAppointmentError && (
            <p className={style.consultTypesHint}>
              Couldn't load this appointment's referrer, so it can't be saved yet.{" "}
              <button
                type="button"
                className={style.consultTypesRetry}
                onClick={() => refetchAppointment()}
              >
                Retry
              </button>
            </p>
          )}
          <div
            style={{
              justifyContent: "space-between",
              width: "100%",
              maxWidth: "100%",
            }}
          >
            <FormField name="notes">
              <label className={style.addLetterFormLabel}>Notes</label>
              <textarea
                className="form-control textareaform-control"
                style={{ minHeight: "100px" }}
                {...form.register("notes")}
              />
            </FormField>
          </div>
          <div className={style.buttonGroup}>
            <Button
              variant="outline"
              size="large"
              disabled={isPending}
              onClick={() => {
                closeModal();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="large"
              disabled={
                isPending ||
                isLoadingConsultTypes ||
                isLoadingConsultTimes ||
                isReferrerUnknown
              }
              onClick={form.handleSubmit(handleFormSubmit, handleInvalidSubmit)}
              style={{ position: "relative" }}
            >
              {isPending ? (
                <>
                  <span className={style.spinner}></span>
                  Saving...
                </>
              ) : (
                "Confirm"
              )}
            </Button>
          </div>
        </form>
      </FormProvider>
      <ConfirmDialog
        isOpen={!!pendingValues}
        onClose={handleCancelOverlap}
        onConfirm={handleConfirmOverlap}
        isLoading={isPending}
        title="Overlapping appointment"
        message={`This appointment overlaps ${overlapCount} existing appointment${
          overlapCount === 1 ? "" : "s"
        } for this doctor. Book anyway?`}
        confirmLabel="Book anyway"
        // Sits above the "Book a slot time" modal (default Dialog zIndex 1300)
        // so its overlay covers the form instead of rendering behind it.
        zIndex={1400}
      />
    </>
  );
};
